import { takeEvery, put, select, take, call, fork } from "redux-saga/effects";
import {
  selectAccountId,
  selectAccountMetaSynced,
  selectAccountUuid
} from "reducers/account/meta";
import { getProfileAttributes } from "helpers/api";
import { reportError } from "helpers/error-reporting";
import { getPropertyDisplayName } from "utils";
import * as actionTypes from "constants/actionTypes";
import { selectAccountProfileAttribute } from "reducers/account/profileAttributes";
import { deleteAccountProfileAttributeInStore } from "actions/profileAttributes";
import * as firebase from "sagas/firebase/lib";
import { throttleBy } from "utils/sagas";
import { REPLACE_ACCOUNT_META } from "constants/account/meta";

export function* replaceAccountProfileAttributes(res) {
  const { data } = res;

  /*
   * Postgres is the primary source of truth for Properties and Events,
   * but users can add custom metadata which we store in Firebase (since
   * the values in Postgres are both in the wrong format and immutable)
   *
   * The following logic reconciles those two data sources
   */

  const accountMetaSynced = yield select(selectAccountMetaSynced);

  if (!accountMetaSynced) {
    yield take(REPLACE_ACCOUNT_META);
  }

  const accountId = yield select(selectAccountId);
  const accountUUID = yield select(selectAccountUuid);
  let apiProfileAttributes;
  try {
    apiProfileAttributes = yield call(
      getProfileAttributes,
      accountId,
      accountUUID
    );
  } catch (err) {
    apiProfileAttributes = {};
    reportError(err);
  }

  const firebaseProfileAttributesByName = Object.values(data || {}).reduce(
    (acc, attribute) => {
      const { name } = attribute;
      acc[name] = attribute;
      return acc;
    },
    {}
  );

  const mergedProfileAttributes = Object.keys(
    apiProfileAttributes || {}
  ).reduce((acc, attributeName) => {
    const firebaseProfileAttribute =
      firebaseProfileAttributesByName[attributeName];

    const lastSeenTs = Math.max(
      ...Object.values(apiProfileAttributes[attributeName] || {}).map(
        it => it.lastSeenTs
      )
    );

    if (firebaseProfileAttribute) {
      acc[firebaseProfileAttribute.id] = {
        ...firebaseProfileAttribute,
        source: "firebase",
        lastSeenTs,
        values: Object.keys(apiProfileAttributes[attributeName] || {})
      };
    } else {
      acc[attributeName] = {
        source: "api",
        description: "",
        name: attributeName,
        id: attributeName,
        label: getPropertyDisplayName(attributeName),
        lastSeenTs,
        showInUI: true,
        values: Object.keys(apiProfileAttributes[attributeName] || {})
      };
    }

    return acc;
  }, {});

  yield put({
    type: actionTypes.REPLACE_ACCOUNT_PROFILE_ATTRIBUTES,
    payload: mergedProfileAttributes
  });
}

export function* replaceAccountProfileAttribute(res) {
  const { key: attributeId, data } = res;

  if (yield select(selectAccountProfileAttribute, data.name)) {
    yield put(deleteAccountProfileAttributeInStore(data.name));
  }

  yield put({
    type: actionTypes.REPLACE_ACCOUNT_PROFILE_ATTRIBUTE,
    payload: {
      attributeId,
      attribute: data
    }
  });
}

function* flushProfileAttributeToFirebase(action) {
  const { attributeId } = action.payload;

  const accountId = yield select(selectAccountId);
  const profileAttribute = yield select(
    selectAccountProfileAttribute,
    attributeId
  );

  if (profileAttribute) {
    yield call(
      firebase.set,
      `accounts/${accountId}/profile-attributes/${attributeId}`,
      profileAttribute
    );
  } else {
    yield call(firebase.remove, [
      `accounts/${accountId}/profile-attributes/${attributeId}`
    ]);
  }
}

export function* createProfileAttributeInFirebase(profileAttribute) {
  const accountId = yield select(selectAccountId);
  return yield call(firebase.push, {
    [`accounts/${accountId}/profile-attributes`]: profileAttribute
  });
}

export default function* profileAttributes() {
  yield takeEvery(
    actionTypes.CREATE_PROFILE_ATTRIBUTE_IN_FIREBASE,
    createProfileAttributeInFirebase
  );

  yield fork(
    throttleBy,
    "attributeId",
    500,
    actionTypes.FLUSH_PROFILE_ATTRIBUTE_TO_FIREBASE,
    flushProfileAttributeToFirebase
  );
}
